import { convexQuery } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { IconOffice2Outline18 } from 'nucleo-ui-outline-18'
import { ActivitySessionFeed } from '~/components/admin/activity-session-feed'
import { WorkspaceContentTab } from '~/components/admin/workspace-content-tab'
import { WorkspaceMembersTab } from '~/components/admin/workspace-members-tab'
import { WorkspaceOverviewTab } from '~/components/admin/workspace-overview-tab'
import { EmptyState } from '~/components/ui/empty-state'
import { BackLink, PageHeader } from '~/components/ui/page-header'
import { SegmentedControl, SegmentedControlItem } from '~/components/ui/segmented-control'
import { Spinner } from '~/components/ui/spinner'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'

const TABS = ['overview', 'members', 'activity', 'content'] as const
type Tab = (typeof TABS)[number]

const parseTab = (value: unknown): Tab =>
  TABS.includes(value as Tab) ? (value as Tab) : 'overview'

export const Route = createFileRoute('/admin/workspaces/$workspaceId')({
  component: AdminWorkspaceDetail,
  head: () => ({ meta: [{ title: titleFor(msg`Workspace`) }] }),
  validateSearch: (search): { tab?: Tab } =>
    search.tab === undefined ? {} : { tab: parseTab(search.tab) },
})

function AdminWorkspaceDetail() {
  const { t } = useLingui()
  const { workspaceId } = Route.useParams()
  const { tab = 'overview' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: workspace } = useQuery(
    convexQuery(api.admin.getWorkspace, { workspaceId: workspaceId as Id<'workspaces'> })
  )

  const labels: Record<Tab, string> = {
    activity: t`Activity`,
    content: t`Content`,
    members: t`Members`,
    overview: t`Overview`,
  }

  if (workspace === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (workspace === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          description={t`It may have been purged.`}
          icon={IconOffice2Outline18}
          title={t`Workspace not found`}
        />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        above={<BackLink to="/admin/workspaces">{t`Workspaces`}</BackLink>}
        below={
          // The strip scrolls sideways rather than wrapping — four tabs in
          // pt-BR outrun a phone's width, and a wrapped tab row reads as two
          // separate controls.
          <div className="flex shrink-0 overflow-x-auto px-5 pb-3">
            <SegmentedControl
              onValueChange={(next) => navigate({ search: { tab: next } })}
              value={tab}
            >
              {TABS.map((value) => (
                <SegmentedControlItem key={value} value={value}>
                  {labels[value]}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          </div>
        }
        title={workspace.name}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'overview' ? <WorkspaceOverviewTab workspace={workspace} /> : null}
        {tab === 'members' ? <WorkspaceMembersTab workspace={workspace} /> : null}
        {tab === 'activity' ? (
          <div className="px-5 py-4">
            <ActivitySessionFeed
              scope={{ kind: 'workspace', workspaceId: workspace._id }}
              showWorkspace={false}
            />
          </div>
        ) : null}
        {tab === 'content' ? <WorkspaceContentTab workspaceId={workspace._id} /> : null}
      </div>
    </div>
  )
}
